/**
 * components/HistoryPanel.jsx
 * Professional redesign — rich card list, skeleton loading, batch support,
 * inline FileDetailModal + LifecycleModal, search + filter.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import API from '../services/api';
import FileDetailModal from './FileDetailModal';
import LifecycleModal from './LifecycleModal';
import '../styles/components.css';

/* ── UTC parse helper — backend sends naive UTC strings without 'Z' ── */
const toUTC = iso => iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z') : null;

/* ── Date helpers ──────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - toUTC(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* ── Status badge ──────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = {
    completed:  { bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
    failed:     { bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
    processing: { bg: '#FFF7ED', color: '#C2410C', dot: '#F97316' },
    pending:    { bg: '#F5F3FF', color: '#5B21B6', dot: '#7C3AED' },
  }[status] || { bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

/* ── Approval badge ────────────────────────────────────────────── */
function ApprovalBadge({ status }) {
  if (!status || status === 'pending') return null;
  const s = {
    approved:       { bg: '#ECFDF5', color: '#065F46', label: 'Approved' },
    self_approved:  { bg: '#F0FDF4', color: '#166534', label: 'Self Approved' },
    rejected:       { bg: '#FEF2F2', color: '#991B1B', label: 'Rejected' },
    pending_review: { bg: '#FFF7ED', color: '#92400E', label: 'In Review' },
  }[status] || { bg: '#F3F4F6', color: '#6B7280', label: status };
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

/* ── File icon (single) ────────────────────────────────────────── */
function FileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}

/* ── Batch icon ────────────────────────────────────────────────── */
function BatchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3"  width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}

/* ── Skeleton card ─────────────────────────────────────────────── */
function SkeletonCard({ index = 0 }) {
  return (
    <div className="hp-card-skel" style={{ animationDelay: `${index * 80}ms` }}>
      <div className="hp-skel-icon" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="hp-skel-line" style={{ width: '45%', height: 14 }} />
        <div className="hp-skel-line" style={{ width: '30%', height: 11 }} />
        <div className="hp-skel-line" style={{ width: '60%', height: 11 }} />
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div className="hp-skel-btn" />
        <div className="hp-skel-btn" />
        <div className="hp-skel-btn" />
      </div>
    </div>
  );
}

/* ── Download dropdown ─────────────────────────────────────────── */
function DownloadMenu({ menuKey, openKey, onToggle, onExcel, onPdf, label = '↓ Report ▾' }) {
  return (
    <div style={{ position: 'relative' }} data-dl-menu>
      <button className="hp-btn hp-btn-dl" onClick={e => onToggle(menuKey, e)}>{label}</button>
      {openKey === menuKey && (
        <div className="hp-dl-menu" data-dl-menu>
          <button className="hp-dl-item" onMouseOver={e => e.currentTarget.style.background = '#F8FAFC'} onMouseOut={e => e.currentTarget.style.background = '#fff'} onClick={onExcel}>Excel (.xlsx)</button>
          <button className="hp-dl-item" onMouseOver={e => e.currentTarget.style.background = '#F8FAFC'} onMouseOut={e => e.currentTarget.style.background = '#fff'} onClick={onPdf}>PDF (.pdf)</button>
        </div>
      )}
    </div>
  );
}

/* ── Empty state ───────────────────────────────────────────────── */
function EmptyState({ filtered }) {
  return (
    <div className="hp-empty">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1C4B8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <div style={{ marginTop: 12, fontWeight: 600, color: '#92745A', fontSize: 15 }}>
        {filtered ? 'No matching scans' : 'No scans yet'}
      </div>
      <div style={{ marginTop: 4, color: '#B09A87', fontSize: 13 }}>
        {filtered ? 'Try adjusting your search or filter.' : 'Upload your first drawing to get started.'}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */
const HistoryPanel = () => {
  // Seed from cache so the list renders instantly on navigation
  const [history,         setHistory]         = useState(() => API.getCached('history') || []);
  const [loading,         setLoading]         = useState(() => !API.getCached('history'));
  const [error,           setError]           = useState(null);
  const [search,          setSearch]          = useState('');
  const [statusFilter,    setStatusFilter]    = useState('all');
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [selectedId,      setSelectedId]      = useState(null);   // opens FileDetailModal
  const [lcDrawing,       setLcDrawing]       = useState(null);   // { drawingId, drawingName }
  const [downloadOpenId,  setDownloadOpenId]  = useState(null);
  const [deletingIds,     setDeletingIds]     = useState(new Set());

  /* ── Fetch history ─────────────────────────────────────────────── */
  const fetchHistory = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await API.getHistory(0, 200);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.error || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we already have cached data, do a silent background refresh
    // so the user sees content immediately while we check for updates.
    const hasCached = API.getCached('history');
    fetchHistory(!hasCached ? false : true);
  }, [fetchHistory]);

  /* ── Close download menu on outside click ──────────────────────── */
  useEffect(() => {
    if (!downloadOpenId) return;
    const handler = (e) => {
      if (!e.target.closest('[data-dl-menu]')) setDownloadOpenId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [downloadOpenId]);

  /* ── Derived: filtered list ────────────────────────────────────── */
  const filtered = history.filter(item => {
    const name = (item.display_name || item.file_name || '').toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchSearch && matchStatus;
  });

  /* ── Derived: stats ────────────────────────────────────────────── */
  const totalEntries  = history.length;
  const completedCount = history.filter(i => i.status === 'completed').length;
  const batchCount    = history.filter(i => i.is_batch).length;
  const totalItems    = history.reduce((sum, i) => sum + (i.item_count || 0), 0);

  /* ── Toggle batch expand ────────────────────────────────────────── */
  const toggleBatch = (batchId, e) => {
    e?.stopPropagation();
    setExpandedBatches(prev => {
      const next = new Set(prev);
      next.has(batchId) ? next.delete(batchId) : next.add(batchId);
      return next;
    });
  };

  /* ── Toggle download menu ───────────────────────────────────────── */
  const toggleDl = (key, e) => {
    e?.stopPropagation();
    setDownloadOpenId(prev => (prev === key ? null : key));
  };

  /* ── Download single file ───────────────────────────────────────── */
  const dlExcel = async (id, name) => {
    setDownloadOpenId(null);
    const raw = (name || 'inspection_plan').replace(/\.[^.]+$/, '');
    const base = raw.trim().replace(/[\\/:*?"<>|]/g, '_');
    try { await API.downloadExcel(id, `${base} - Inspection Plan.xlsx`); } catch (e) { setError(e.error || 'Download failed'); }
  };
  const dlPdf = async (id, name) => {
    setDownloadOpenId(null);
    const raw = (name || 'inspection_plan').replace(/\.[^.]+$/, '');
    const base = raw.trim().replace(/[\\/:*?"<>|]/g, '_');
    try { await API.downloadPdf(id, `${base} - Inspection Plan.pdf`); } catch (e) { setError(e.error || 'PDF download failed'); }
  };

  /* ── Download batch ZIP ─────────────────────────────────────────── */
  const dlBatchZip = async (type, fileIds) => {
    setDownloadOpenId(null);
    try {
      if (type === 'excel') await API.downloadZipExcel(fileIds);
      else await API.downloadZipPdf(fileIds);
    } catch (e) { setError(e.error || 'Zip download failed'); }
  };

  /* ── Delete single item ─────────────────────────────────────────── */
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await API.deleteHistoryItem(id);
      setHistory(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      setError(e.error || 'Delete failed');
    } finally {
      setDeletingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  /* ── Delete entire batch ────────────────────────────────────────── */
  const handleDeleteBatch = async (batchId, fileIds, name) => {
    if (!window.confirm(`Delete all ${fileIds.length} files in "${name}"? This cannot be undone.`)) return;
    fileIds.forEach(id => setDeletingIds(prev => new Set(prev).add(id)));
    try {
      await API.deleteSelectedHistoryItems(fileIds);
      setHistory(prev => prev.filter(i => i.batch_id !== batchId));
    } catch (e) {
      setError(e.error || 'Delete failed');
    } finally {
      setDeletingIds(prev => { const n = new Set(prev); fileIds.forEach(id => n.delete(id)); return n; });
    }
  };

  /* ══════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════ */
  return (
    <div className="hp-wrap">

      {/* Modals */}
      {selectedId && (
        <FileDetailModal
          fileId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
      {lcDrawing && (
        <LifecycleModal
          drawingId={lcDrawing.drawingId}
          drawingName={lcDrawing.drawingName}
          onClose={() => setLcDrawing(null)}
        />
      )}

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="hp-header">
        <div className="hp-header-left">
          <h1 className="hp-title">Scan History</h1>
          {!loading && (
            <div className="hp-stats-row">
              <span className="hp-stat-chip">{totalEntries} total</span>
              <span className="hp-stat-sep">·</span>
              <span className="hp-stat-chip hp-stat-green">{completedCount} completed</span>
              {batchCount > 0 && <>
                <span className="hp-stat-sep">·</span>
                <span className="hp-stat-chip hp-stat-amber">{batchCount} batch{batchCount !== 1 ? 'es' : ''}</span>
              </>}
              {totalItems > 0 && <>
                <span className="hp-stat-sep">·</span>
                <span className="hp-stat-chip">{totalItems.toLocaleString()} items</span>
              </>}
            </div>
          )}
        </div>
        <button className="hp-btn hp-btn-ghost" onClick={() => fetchHistory(false)} disabled={loading}>
          {loading ? '…' : '↺ Refresh'}
        </button>
      </div>

      {/* ── Error banner ────────────────────────────────────────────── */}
      {error && (
        <div className="hp-error-banner">
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 10, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>×</button>
        </div>
      )}

      {/* ── Search + filter pills ───────────────────────────────────── */}
      <div className="hp-controls">
        <div className="hp-search-wrap">
          <svg className="hp-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="hp-search"
            placeholder="Search by file name or part number…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="hp-search-clear" onClick={() => setSearch('')}>×</button>}
        </div>
        <div className="hp-filter-pills">
          {['all', 'completed', 'processing', 'failed'].map(f => (
            <button
              key={f}
              className={`hp-pill ${statusFilter === f ? 'hp-pill-active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="hp-list">
          {[0, 1, 2, 3, 4].map(i => <SkeletonCard key={i} index={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState filtered={!!(search || statusFilter !== 'all')} />
      ) : (
        <div className="hp-list">
          {filtered.map((item, idx) => {

            /* ── BATCH CARD ─────────────────────────────────────── */
            if (item.is_batch) {
              const isExpanded     = expandedBatches.has(item.batch_id);
              const completedFiles = (item.files || []).filter(f => f.status === 'completed');
              const allFileIds     = (item.files || []).map(f => f.id);
              const dlKey          = `hp-b-${item.batch_id}`;
              const isBatchDel     = allFileIds.some(id => deletingIds.has(id));
              const displayName    = item.display_name || item.file_name || `Batch (${item.file_count} files)`;

              return (
                <React.Fragment key={`batch-${item.batch_id}`}>
                  {/* Batch header card */}
                  <div
                    className="hp-card hp-card-batch"
                    style={{ animationDelay: `${idx * 40}ms`, cursor: 'pointer' }}
                    onClick={e => toggleBatch(item.batch_id, e)}
                  >
                    {/* Left: icon + toggle */}
                    <div className="hp-card-icon hp-card-icon-batch">
                      <span className="hp-toggle-arrow">{isExpanded ? '▼' : '▶'}</span>
                      <BatchIcon />
                    </div>

                    {/* Middle: info */}
                    <div className="hp-card-body">
                      <div className="hp-card-name">
                        {displayName}
                        <span className="hp-file-count-badge">{item.file_count} files</span>
                      </div>
                      <div className="hp-meta">
                        <span>{fmtDateTime(item.upload_timestamp)}</span>
                        {item.cc_count != null && <>
                          <span className="hp-meta-dot">·</span>
                          <span>{item.cc_count} CCs</span>
                          <span className="hp-meta-dot">·</span>
                          <span>{item.item_count} items</span>
                        </>}
                        {item.completed_timestamp && <>
                          <span className="hp-meta-dot">·</span>
                          <span>Completed {fmtDate(item.completed_timestamp)}</span>
                        </>}
                      </div>
                      {/* Reviewer info */}
                      {item.reviewer_name && (
                        <div className="hp-reviewer-row">
                          <span className="hp-reviewer-avatar">{item.reviewer_name[0]?.toUpperCase()}</span>
                          <span className="hp-reviewer-text">{item.reviewer_name}</span>
                          {item.reviewer_email && <span className="hp-reviewer-email">({item.reviewer_email})</span>}
                        </div>
                      )}
                    </div>

                    {/* Right: badges + actions */}
                    <div className="hp-card-right" onClick={e => e.stopPropagation()}>
                      <div className="hp-badge-row">
                        <StatusBadge status={item.status} />
                        <ApprovalBadge status={item.approval_status} />
                      </div>
                      <div className="hp-actions">
                        {item.status === 'completed' && completedFiles.length > 0 && (
                          <DownloadMenu
                            menuKey={dlKey}
                            openKey={downloadOpenId}
                            onToggle={toggleDl}
                            label="↓ ZIP ▾"
                            onExcel={() => dlBatchZip('excel', completedFiles.map(f => f.id))}
                            onPdf={() => dlBatchZip('pdf', completedFiles.map(f => f.id))}
                          />
                        )}
                        <button
                          className="hp-btn hp-btn-danger"
                          disabled={isBatchDel}
                          onClick={e => { e.stopPropagation(); handleDeleteBatch(item.batch_id, allFileIds, displayName); }}
                        >
                          {isBatchDel ? '…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded child file rows */}
                  {isExpanded && (item.files || []).map((file, fi) => {
                    const fDlKey     = `hp-f-${file.id}`;
                    const fName      = file.display_name || file.file_name;
                    const isFileDel  = deletingIds.has(file.id);
                    return (
                      <div
                        key={`file-${file.id}`}
                        className="hp-card hp-card-child"
                        style={{ animationDelay: `${(idx + fi + 1) * 40}ms`, cursor: 'pointer' }}
                        onClick={() => setSelectedId(file.id)}
                      >
                        <div className="hp-child-indent" />
                        <div className="hp-card-icon hp-card-icon-child">
                          <FileIcon />
                        </div>
                        <div className="hp-card-body">
                          <div className="hp-card-name" style={{ fontSize: 13.5 }}>{fName}</div>
                          <div className="hp-meta">
                            <span>{fmtDateTime(file.upload_timestamp)}</span>
                            {file.cc_count != null && <>
                              <span className="hp-meta-dot">·</span>
                              <span>{file.cc_count} CCs · {file.item_count} items</span>
                            </>}
                          </div>
                        </div>
                        <div className="hp-card-right" onClick={e => e.stopPropagation()}>
                          <div className="hp-badge-row">
                            <StatusBadge status={file.status} />
                            <ApprovalBadge status={file.approval_status} />
                          </div>
                          <div className="hp-actions">
                            <button className="hp-btn hp-btn-primary" onClick={() => setSelectedId(file.id)}>View</button>
                            {file.status === 'completed' && (
                              <DownloadMenu
                                menuKey={fDlKey}
                                openKey={downloadOpenId}
                                onToggle={toggleDl}
                                label="↓ ▾"
                                onExcel={() => dlExcel(file.id, fName)}
                                onPdf={() => dlPdf(file.id, fName)}
                              />
                            )}
                            <button
                              className="hp-btn hp-btn-ghost"
                              style={{ fontSize: 11 }}
                              onClick={() => setLcDrawing({ drawingId: file.id, drawingName: fName })}
                            >
                              Lifecycle
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            }

            /* ── SINGLE FILE CARD ───────────────────────────────── */
            const dlKey   = `hp-${item.id}`;
            const name    = item.display_name || item.file_name;
            const isDel   = deletingIds.has(item.id);

            return (
              <div
                key={item.id}
                className="hp-card hp-card-single"
                style={{ animationDelay: `${idx * 40}ms`, cursor: 'pointer' }}
                onClick={() => setSelectedId(item.id)}
              >
                {/* Left: icon */}
                <div className="hp-card-icon">
                  <FileIcon />
                </div>

                {/* Middle: info */}
                <div className="hp-card-body">
                  <div className="hp-card-name">{name}</div>
                  <div className="hp-meta">
                    <span>{fmtDateTime(item.upload_timestamp)}</span>
                    {item.cc_count != null && <>
                      <span className="hp-meta-dot">·</span>
                      <span>{item.cc_count} CCs</span>
                      <span className="hp-meta-dot">·</span>
                      <span>{item.item_count} items</span>
                    </>}
                    {item.completed_timestamp && <>
                      <span className="hp-meta-dot">·</span>
                      <span className="hp-meta-timeago">Completed {timeAgo(item.completed_timestamp)}</span>
                    </>}
                  </div>
                  {/* Reviewer row */}
                  {item.reviewer_name && (
                    <div className="hp-reviewer-row">
                      <span className="hp-reviewer-avatar">{item.reviewer_name[0]?.toUpperCase()}</span>
                      <span className="hp-reviewer-text">{item.reviewer_name}</span>
                      {item.reviewer_email && <span className="hp-reviewer-email">({item.reviewer_email})</span>}
                    </div>
                  )}
                  {/* Multi-reviewer chips if applicable */}
                  {item.reviewers && item.reviewers.length > 1 && (
                    <div className="hp-reviewers-row">
                      {item.reviewers.map((r, ri) => (
                        <span key={ri} className={`hp-reviewer-chip hp-reviewer-chip-${r.status === 'approved' ? 'green' : r.status === 'rejected' ? 'red' : 'amber'}`}>
                          {r.name || r.email} · {r.status}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right: badges + actions */}
                <div className="hp-card-right" onClick={e => e.stopPropagation()}>
                  <div className="hp-badge-row">
                    <StatusBadge status={item.status} />
                    <ApprovalBadge status={item.approval_status} />
                  </div>
                  <div className="hp-actions">
                    <button className="hp-btn hp-btn-primary" onClick={() => setSelectedId(item.id)}>View</button>
                    {item.status === 'completed' && (
                      <DownloadMenu
                        menuKey={dlKey}
                        openKey={downloadOpenId}
                        onToggle={toggleDl}
                        onExcel={() => dlExcel(item.id, name)}
                        onPdf={() => dlPdf(item.id, name)}
                      />
                    )}
                    <button
                      className="hp-btn hp-btn-ghost"
                      style={{ fontSize: 11 }}
                      onClick={() => setLcDrawing({ drawingId: item.id, drawingName: name })}
                    >
                      Lifecycle
                    </button>
                    <button
                      className="hp-btn hp-btn-danger"
                      disabled={isDel}
                      onClick={() => handleDelete(item.id, name)}
                    >
                      {isDel ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistoryPanel;
